/**
 * PJ Sandbox（PJ 予定シミュレーション白板）の永続化 API 契約 — フロント/バック共有の単一 SoT。
 *
 * リクエスト/レスポンスの Zod schema・推論型・編集権限の述語をここに集約し、
 * - API の OpenAPI schema (`apps/api/product/src/features/pj-sandbox/api/schemas.ts`)
 * - API の Firestore 読み取り検証 (`infrastructure/firestore-pj-sandbox-repository.ts`)
 * - Web の API クライアント/フック (`apps/web/product-innovation-lab/src/hooks/usePjSandbox.ts`)
 * がすべてこの 1 ファイルから import する。契約が二重定義されないようにするため
 * (architecture.md「API クライアント契約」: 自前 API の req/res 型は package 側を単一 SoT とする)。
 *
 * 【永続化の座標系について】白板の実行時モデル（`SandboxBlock.placement`）は
 * `rowIndex`（members[] の添字）と `startDay`（days[] の添字）という **相対添字** で位置を持つ。
 * これは描画には都合が良いが、そのまま保存すると次の 2 つの理由で意味が壊れる:
 *
 * 1. `rowIndex` は戦闘力 API 由来のメンバー一覧の並び順に依存する。メンバーが入退社・
 *    異動すると添字がずれ、保存済みブロックが **別人の行** を指してしまう。
 * 2. `startDay` は「先週の月曜」を 0 とする相対添字。翌日に開き直すと同じ添字が
 *    **別の日付** を意味し、白板全体が 1 日ぶん過去へずれる。
 *
 * そこで永続化層では位置を **安定した識別子**（メンバーのニックネーム + ISO 日付）で持ち、
 * 読み込み時に現在の members[] / days[] へ解決し直す。解決できないブロック（退職者・
 * 表示期間外の日付）は未配置トレイへ落とし、誤った行/日付に置き続けないようにする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox 白板の共有保存 API 契約 (Zod schema + 推論型 + 編集権限述語) の単一 SoT
 * @graph-connects none
 */
import { z } from 'zod';
/**
 * 白板（チーム）の key。タブの並び順と一致し、Firestore のドキュメント ID にもなる。
 * `apps/web/.../pj-sandbox/teamRoster.ts` の `TEAM_DEFS` はこの配列を SSOT として参照する
 * （リテラルの重複宣言を禁止するため）。
 * @graph-connects none
 */
export const PJ_SANDBOX_BOARD_KEYS = ['pdg', 'ace-ac', 'ace-platform', 'ace-mall'];
/** @graph-connects none */
const PJ_SANDBOX_BOARD_KEY_SET = new Set(PJ_SANDBOX_BOARD_KEYS);
/**
 * 任意の値が白板 key かを判定する型ガード。route の param 検証で使う。
 * @graph-connects none
 */
export function isPjSandboxBoardKey(value) {
    return typeof value === 'string' && PJ_SANDBOX_BOARD_KEY_SET.has(value);
}
/** 白板 key の Zod schema。@graph-connects none */
export const pjSandboxBoardKeySchema = z.enum(PJ_SANDBOX_BOARD_KEYS);
/**
 * 共有「未配置トレイ」の擬似 boardKey。チーム白板と同じ `pj_sandbox_boards/{key}` の
 * 1 ドキュメントとして保存するが、**どのチームにも属さない全体プール** を表す。
 *
 * 未配置ブロック（`placement === null`）は「まだ誰の・いつの予定でもない案件」であり、
 * チーム白板を切り替えるたびに中身が入れ替わると、同じ案件を白板ごとに作り直すことになる。
 * そのため未配置ブロックだけをこの 1 ドキュメントへ集約し、どのチームを見ていても
 * 同じ内容が並ぶようにする（配置済みブロックと候補案件は従来どおりチーム白板ごと）。
 *
 * タブの並び (`PJ_SANDBOX_BOARD_KEYS`) には **含めない**。含めるとチーム名簿を持たない
 * 白板がタブに現れてしまう。
 * @graph-connects none
 */
export const PJ_SANDBOX_SHARED_TRAY_KEY = 'shared-tray';
/**
 * 保存ドキュメントとして許可される key の全体（チーム白板 + 共有未配置トレイ）。
 * API の path param 検証はこちらを使い、Firestore に野良ドキュメントが増えないようにする。
 * @graph-connects none
 */
export const PJ_SANDBOX_DOCUMENT_KEYS = [
    ...PJ_SANDBOX_BOARD_KEYS,
    PJ_SANDBOX_SHARED_TRAY_KEY,
];
/** @graph-connects none */
const PJ_SANDBOX_DOCUMENT_KEY_SET = new Set(PJ_SANDBOX_DOCUMENT_KEYS);
/**
 * 任意の値が保存ドキュメント key かを判定する型ガード。
 * @graph-connects none
 */
export function isPjSandboxDocumentKey(value) {
    return typeof value === 'string' && PJ_SANDBOX_DOCUMENT_KEY_SET.has(value);
}
/** 保存ドキュメント key の Zod schema。@graph-connects none */
export const pjSandboxDocumentKeySchema = z.enum(PJ_SANDBOX_DOCUMENT_KEYS);
/** ブロック種別。実行時モデル（`pj-sandbox/types.ts` の `BlockKind`）と一致させる。@graph-connects none */
export const pjSandboxBlockKindSchema = z.enum(['dev', 'design', 'bug']);
/** ISO 日付 (YYYY-MM-DD, ローカル暦)。@graph-connects none */
const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/**
 * 保存されるブロックの配置。実行時の `{ rowIndex, startDay, spanDays }` ではなく
 * 「誰の行か」をニックネーム、「いつからか」を ISO 日付で持つ（冒頭の座標系メモ参照）。
 * @graph-connects none
 */
export const pjSandboxStoredPlacementSchema = z.object({
    /** 配置先メンバーのニックネーム（小文字。members[].nickName と突き合わせる）。 */
    memberNickName: z.string().min(1).max(80),
    /** 開始日 (YYYY-MM-DD, ローカル暦)。 */
    startDate: isoDateSchema,
    /** 跨ぐ日数 (>=1)。 */
    spanDays: z.number().int().min(1).max(90),
});
/** 保存されるブロック 1 件。@graph-connects none */
export const pjSandboxStoredBlockSchema = z.object({
    /** 白板内で一意なブロック ID。 */
    id: z.string().min(1).max(64),
    /** 表示名。 */
    name: z.string().min(1).max(200),
    /** 見積もりサイズ (pt)。未入力の取込ブロックは 0。 */
    sizePt: z.number().min(0).max(1000),
    /** 種別（色に対応）。 */
    kind: pjSandboxBlockKindSchema,
    /** 配置。null は未配置トレイに滞留していることを表す。 */
    placement: pjSandboxStoredPlacementSchema.nullable(),
    /** ドラッグ元の候補案件 ID（候補プールから作られたブロックのみ）。 */
    sourceCandidateId: z.string().min(1).max(64).optional(),
    /** 紐づく Backlog 課題キー（例: AIRCLOSET-137048）。 */
    issueKey: z.string().min(1).max(64).optional(),
});
/** 保存される候補案件 1 件。@graph-connects none */
export const pjSandboxStoredCandidateSchema = z.object({
    /** 白板内で一意な候補 ID。 */
    id: z.string().min(1).max(64),
    /** 案件名。 */
    title: z.string().min(1).max(200),
    /** 見積もりサイズ (pt)。 */
    sizePt: z.number().min(0).max(1000),
    /** 種別（色に対応）。 */
    kind: pjSandboxBlockKindSchema,
    /**
     * 着手予定週の **月曜の ISO 日付**。実行時の `weekIndex`（今週=0 の相対添字）は週をまたぐと
     * 意味が変わるため、絶対日付で保存して読み込み時に添字へ解決する。
     */
    weekStartDate: isoDateSchema,
    /** 紐づく Backlog 課題キー。 */
    issueKey: z.string().min(1).max(64).optional(),
});
/**
 * 編集履歴に記録する操作の種別。表示ラベルは web 側の i18n
 * (`pjSandbox.history.action.*`) が持ち、ここでは locale 非依存の識別子だけを定義する。
 * @graph-connects none
 */
export const PJ_SANDBOX_HISTORY_ACTIONS = [
    /** 未配置ブロックを新規作成した */
    'block_create',
    /** 候補案件を白板へ配置した（新規ブロック生成を伴う） */
    'block_place',
    /** 配置済みブロックを別の行 / 日付へ動かした */
    'block_move',
    /** 配置済みブロックの期間（跨ぐ日数）を変えた */
    'block_resize',
    /** ブロックの属性（名称 / pt / 種別 / 案件番号）を編集した */
    'block_edit',
    /** ブロックを削除した */
    'block_delete',
    /** 未配置ブロックを候補案件プールの週へ戻した */
    'block_to_candidate',
    /** 候補案件を削除した */
    'candidate_delete',
    /** 候補案件を別の週セクションへ動かした */
    'candidate_move_week',
    /** 案件番号で複数チケットを一括取込した */
    'ticket_import',
];
/** @graph-connects none */
export const pjSandboxHistoryActionSchema = z.enum(PJ_SANDBOX_HISTORY_ACTIONS);
/**
 * 保存時にクライアントが送る操作 1 件。
 *
 * **実行者（誰が）と時刻（いつ）はここに含めない** — サーバーが認証済み ID とサーバー時刻で
 * 刻む。クライアントが送るのは「何をしたか」だけなので、履歴の帰属を詐称できない。
 * @graph-connects none
 */
export const pjSandboxOperationInputSchema = z.object({
    /** 操作種別。 */
    action: pjSandboxHistoryActionSchema,
    /** 対象の表示名（ブロック名 / 案件名）。一括取込のように対象が複数のときは空文字。 */
    targetName: z.string().max(200),
    /**
     * 変更前の値を表す locale 非依存の短いトークン。
     * 移動なら `"ray 8/31"`、期間変更なら `"3"`（日数）、属性編集なら `"検索リプレイス 20pt"`。
     * 表示は web 側の i18n テンプレートが行う。該当しない操作では null。
     */
    before: z.string().max(120).nullable(),
    /** 変更後の値。`before` と同じ書式。@see before */
    after: z.string().max(120).nullable(),
    /** 一括取込など、件数が意味を持つ操作の件数。それ以外は null。 */
    count: z.number().int().min(0).max(10000).nullable(),
});
/** 保存済みの編集履歴 1 件（サーバーが実行者・時刻を付与したもの）。@graph-connects none */
export const pjSandboxHistoryEntrySchema = pjSandboxOperationInputSchema.extend({
    /** Firestore ドキュメント ID。ページングのカーソルにも使う。 */
    id: z.string(),
    /** 実行者のメールアドレス（サーバー付与）。 */
    actorEmail: z.string(),
    /** 実行者の表示名。ニックネームがあればそれ、無ければメールのローカル部（サーバー付与）。 */
    actorName: z.string(),
    /** 実行時刻 ISO 文字列（サーバー付与）。 */
    at: z.string(),
});
/**
 * 白板の保存状態（Firestore ドキュメント本体）。
 * 読み取り境界で実データを検証し、壊れた文書をそのままフロントへ返さないために使う。
 * @graph-connects none
 */
export const pjSandboxBoardDocumentSchema = z.object({
    blocks: z.array(pjSandboxStoredBlockSchema),
    candidates: z.array(pjSandboxStoredCandidateSchema),
    /**
     * 楽観ロック用のリビジョン。保存のたびに +1 する。クライアントは読み込んだ
     * リビジョンを添えて保存し、サーバー側で不一致なら 409 を返して上書きを防ぐ。
     */
    revision: z.number().int().min(0),
    /** 最終更新日時 ISO 文字列（未保存時 null）。 */
    updatedAt: z.string().nullable(),
    /** 最終更新者メール（未保存時 null）。 */
    updatedBy: z.string().nullable(),
    /** 最終更新者の表示名（未保存時 null）。 */
    updatedByName: z.string().nullable(),
});
/** `GET /pj-sandbox/boards/{boardKey}` レスポンス。@graph-connects none */
export const pjSandboxBoardResponseSchema = z.object({
    board: pjSandboxBoardDocumentSchema,
    /** 閲覧ユーザーが編集権限を持つか（`canEditPjSandbox` と同基準）。 */
    canEdit: z.boolean(),
});
/**
 * `PUT /pj-sandbox/boards/{boardKey}` リクエスト。白板の状態を全置換で保存し、
 * 同時に今回ぶんの操作を編集履歴へ追記する。
 * @graph-connects none
 */
export const putPjSandboxBoardRequestSchema = z.object({
    /**
     * このクライアントが読み込んだ時点のリビジョン。サーバー側の現在値と異なれば 409。
     * 初回保存（サーバーに文書が無い状態）は 0 を送る。
     */
    baseRevision: z.number().int().min(0),
    blocks: z.array(pjSandboxStoredBlockSchema).max(500),
    candidates: z.array(pjSandboxStoredCandidateSchema).max(300),
    /** 前回保存以降に行われた操作。履歴ブロックに 1 行ずつ並ぶ。 */
    operations: z.array(pjSandboxOperationInputSchema).max(100),
});
/** `GET /pj-sandbox/boards/{boardKey}/history` レスポンス。@graph-connects none */
export const pjSandboxHistoryResponseSchema = z.object({
    /** 新しい順（`at` 降順）。 */
    entries: z.array(pjSandboxHistoryEntrySchema),
    /** さらに古い履歴が残っているか（「もっと見る」の出し分けに使う）。 */
    hasMore: z.boolean(),
});
/** 履歴取得のデフォルト件数。@graph-connects none */
export const PJ_SANDBOX_HISTORY_DEFAULT_LIMIT = 50;
/** 履歴取得の最大件数。@graph-connects none */
export const PJ_SANDBOX_HISTORY_MAX_LIMIT = 200;
/**
 * PJ Sandbox 白板を編集できるポータルロール。PDG/ACE の計画を動かす立場
 * （PMO / チームリーダー / グループマネージャー）に限る。
 * @graph-connects none
 */
const PJ_SANDBOX_EDITOR_ROLES = new Set([
    'pmo',
    'team_leader',
    'group_manager',
]);
/**
 * PJ Sandbox 白板の編集権限（保存を許可するか）を判定する。
 *
 * 白板は全員で共有する 1 枚の状態なので、閲覧は広く開放し、**書き込みだけ**をここで絞る。
 * 許可条件は次のいずれか:
 *
 * 1. cortex 管理者（`admin` ロールを保持）— Div を問わず常に許可
 * 2. `division === 'pi_div'`（PDG / ACE / PMO / IDT が属するエンジニア組織）**かつ**
 *    `pmo` / `team_leader` / `group_manager` のいずれかを保持
 *
 * したがって次はいずれも **閲覧のみ**:
 * - PI Div の一般メンバー（`member` / `architect` / `dle` / `tech_lead` 単独保持）
 * - 横断 PMO（`ac_div` / `mall_div` / `log_div` × `pmo`）— PDG/ACE の白板は動かせない
 * - `external`（portalRole 未設定の全社員フォールバック）
 *
 * 複数ロール保持時は「許可ロールを 1 つでも持つか」の existential チェックであり、
 * 優先順位の概念はない。frontend（ボタン/DnD の出し分け）と backend（PUT の 403）が
 * 同一述語を参照するための SSOT。重複定義は禁止。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox 白板の編集権限ルール (admin / pi_div + pmo|team_leader|group_manager)
 * @graph-connects none
 */
export function canEditPjSandbox(identity) {
    const roles = identity.portalRoles ?? [];
    if (roles.includes('admin'))
        return true;
    if (identity.division !== 'pi_div')
        return false;
    return roles.some((role) => PJ_SANDBOX_EDITOR_ROLES.has(role));
}
//# sourceMappingURL=pj-sandbox.js.map